/**
 * Servicio de perfil profesional — Django REST.
 *
 * Endpoint: /api/me/professional-profile/  (GET / PATCH / PUT, auth requerida)
 *
 * Antes vivía en Supabase (tabla professional_profiles) pero esa tabla nunca
 * se creó y todas las llamadas devolvían 404. El estado del perfil pasó a
 * users.Profile (Django) — single source of truth, mismo flujo de auth que
 * el resto de la API.
 *
 * El shape que devuelve el endpoint es el que el resto del frontend espera:
 *   { user_id, company_name, job_title, country, industry,
 *     corporate_emails, is_corporate_verified, updated_at }
 */
import { apiRequest } from './api/request.js';

/**
 * Obtener perfil profesional del usuario autenticado.
 * El parámetro userId se ignora — el endpoint resuelve por request.user.
 * Lo dejamos en la firma por compatibilidad con DataContext.fetchProfessionalProfile,
 * que todavía lo recibe.
 * @returns {object|null}
 */
export const getProfessionalProfile = async (_userId) => {
    try {
        return await apiRequest('/me/professional-profile/', { requireAuth: true });
    } catch {
        return null;
    }
};

/**
 * Crear o actualizar perfil profesional (upsert por usuario autenticado).
 * @param {{ company_name?: string, job_title?: string, country?: string, industry?: string }} profile
 * @returns {object}
 */
export const upsertProfessionalProfile = async (profile) => {
    const payload = {
        company_name: profile.company_name?.trim() || '',
        job_title: profile.job_title?.trim() || '',
        country: profile.country?.trim() || '',
        industry: profile.industry?.trim() || '',
    };
    return apiRequest('/me/professional-profile/', {
        method: 'PATCH',
        body: JSON.stringify(payload),
        requireAuth: true,
    });
};

/**
 * Mínimo de completitud: empresa + cargo declarados.
 * @param {object|null} profile
 * @returns {boolean}
 */
export const isProfileComplete = (profile) => {
    if (!profile) return false;
    return !!(profile.company_name?.trim() && profile.job_title?.trim());
};
